
import React, { useState } from 'react';
import { PrintOptions } from '../types';
import { X, FileText, CheckSquare, Users, History, Map, PieChart, ShieldAlert, Calendar, QrCode } from 'lucide-react';
import { Button } from './Button';
import { erledigteBilderNurSeitVorbericht, type BerichtDruckOptionen } from '../services/pdf/pdfUtils';

interface Props {
  options: PrintOptions;
  onChange: (options: PrintOptions) => void;
  onClose: () => void;
  /** Begehungsdatum des Berichts — derselbe Resolver wie im Generator (Stichtag der neuen Vorgabe). */
  berichtsDatum?: string | null;
}

export const PrintSettingsModal: React.FC<Props> = ({ options, onChange, onClose, berichtsDatum }) => {
  const predefinedValues = [0, 7, 14, 30, 90, 180, 365];
  const isCustomInitially = options.historyFilterDays !== undefined && options.historyFilterDays !== 0 && !predefinedValues.includes(options.historyFilterDays);
  
  const [showCustomInput, setShowCustomInput] = useState(isCustomInitially);
  const [customDays, setCustomDays] = useState(isCustomInitially ? options.historyFilterDays : 45);

  const toggle = (key: keyof PrintOptions) => {
    onChange({ ...options, [key]: !options[key] });
  };

  // Der Freimelde-QR ist an, solange ihn niemand abwaehlt — `undefined` zaehlt
  // also als AN. Das normale `toggle` wuerde aus `undefined` ein `true` machen
  // und der erste Klick haette keine sichtbare Wirkung.
  const freimeldungQrAn = options.showFreimeldungQr !== false;
  const toggleFreimeldungQr = () => {
    onChange({ ...options, showFreimeldungQr: !freimeldungQrAn });
  };

  // Betreiber-Entscheid 30.09.2026: ältere Erledigte ohne Bilder ist die
  // Vorgabe für alle Projekte. Angezeigt und umgeschaltet wird der WIRKSAME
  // Wert des neuen Schlüssels `closedImagesOnlySinceLastReport` (fehlt er,
  // gilt AN). Der alte `hideClosedImagesAfterNextReport` wird nicht mehr
  // gelesen — warum, steht bei erledigteBilderNurSeitVorbericht (pdfUtils).
  const nurSeitVorberichtAn = erledigteBilderNurSeitVorbericht(options, berichtsDatum);
  const toggleNurSeitVorbericht = () => {
    const neu: BerichtDruckOptionen = { ...options, closedImagesOnlySinceLastReport: !nurSeitVorberichtAn };
    onChange(neu);
  };

  const handleSelectChange = (val: string) => {
    if (val === 'custom') {
      setShowCustomInput(true);
      onChange({ ...options, historyFilterDays: customDays || 45 });
    } else {
      setShowCustomInput(false);
      onChange({ ...options, historyFilterDays: parseInt(val, 10) });
    }
  };

  const handleCustomDaysChange = (days: number) => {
    const cleanDays = isNaN(days) ? 1 : Math.max(1, days);
    setCustomDays(cleanDays);
    onChange({ ...options, historyFilterDays: cleanDays });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
        <div className="bg-slate-900 text-white p-5 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Berichtsinhalt konfigurieren
          </h3>
          <button onClick={onClose} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button>
        </div>
        
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-500 mb-4">Wählen Sie aus, welche Bereiche im PDF-Export enthalten sein sollen.</p>
          
          <div className="space-y-2">
            <OptionRow 
                icon={<Users className="w-4 h-4"/>} 
                label="Verteilerliste & Anwesenheit" 
                checked={options.includeDistribution} 
                onChange={() => toggle('includeDistribution')} 
            />
            <OptionRow 
                icon={<CheckSquare className="w-4 h-4"/>} 
                label="Checklisten (Prüfpunkte)" 
                checked={options.includeChecklists} 
                onChange={() => toggle('includeChecklists')} 
            />
            <OptionRow 
                icon={<FileText className="w-4 h-4"/>} 
                label="Aktuelle Feststellungen (Offen/Info)" 
                checked={options.includeActiveItems} 
                onChange={() => toggle('includeActiveItems')} 
            />
            
            <div className={`rounded-xl border-2 transition-all ${options.includeHistory ? 'border-blue-500 bg-blue-50/50' : 'border-slate-100 hover:border-slate-300'}`}>
                <div data-tutorial="pdf.historie" onClick={() => toggle('includeHistory')} className="flex items-center justify-between p-3 cursor-pointer">
                     <div className="flex items-center gap-3 text-sm font-bold text-slate-700">
                        <div className={`p-1.5 rounded-lg ${options.includeHistory ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                            <History className="w-4 h-4"/>
                        </div>
                        Erledigte Mängel (Historie)
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${options.includeHistory ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
                        {options.includeHistory && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                </div>
                {options.includeHistory && (
                    <div className="px-3 pb-3 pl-12 animate-slide-in space-y-2">
                        <div className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-lg p-1.5">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-500 font-medium">Zeitraum:</span>
                            <select 
                                value={showCustomInput ? 'custom' : (options.historyFilterDays || 0)} 
                                onChange={(e) => handleSelectChange(e.target.value)}
                                className="bg-transparent font-bold text-blue-600 outline-none cursor-pointer flex-1"
                            >
                                <option value={0}>Gesamte Laufzeit</option>
                                <option value={7}>Letzte 7 Tage</option>
                                <option value={14}>Letzte 14 Tage</option>
                                <option value={30}>Letzte 30 Tage</option>
                                <option value={90}>Letzte 3 Monate</option>
                                <option value={180}>Letzte 6 Monate</option>
                                <option value={365}>Letztes Jahr</option>
                                <option value="custom">Benutzerdefiniert...</option>
                            </select>
                        </div>
                        {showCustomInput && (
                            <div className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-lg p-1.5 animate-slide-in">
                                <span className="text-slate-500 font-medium">Anzahl Tage:</span>
                                <input 
                                    type="number" 
                                    min="1"
                                    value={customDays}
                                    onChange={(e) => handleCustomDaysChange(parseInt(e.target.value, 10))}
                                    className="bg-transparent font-bold text-blue-600 outline-none w-16 border-b border-slate-200 focus:border-blue-500 text-center"
                                />
                            </div>
                        )}
                        <div 
                            onClick={toggleNurSeitVorbericht}
                            className="flex items-center gap-2 text-xs bg-white border border-slate-200 hover:border-blue-400 rounded-lg p-1.5 cursor-pointer selection:bg-transparent"
                        >
                            <input 
                                type="checkbox" 
                                checked={nurSeitVorberichtAn} 
                                onChange={() => {}} // toggled by row click
                                className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="text-slate-600 font-medium cursor-pointer">
                                Ältere Erledigte ohne Bilder (Bildnachweis nur seit dem letzten Bericht)
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <OptionRow 
                icon={<QrCode className="w-4 h-4"/>} 
                label="Freimelde-QR (online als behoben melden)" 
                checked={freimeldungQrAn} 
                onChange={toggleFreimeldungQr} 
            />
            <OptionRow 
                icon={<Map className="w-4 h-4"/>} 
                label="Pläne & Verortungen" 
                checked={options.includePlans} 
                onChange={() => toggle('includePlans')} 
            />
            <OptionRow 
                icon={<ShieldAlert className="w-4 h-4"/>} 
                label="Gefährdungs-Matrix (Radar)" 
                checked={options.includeHazards} 
                onChange={() => toggle('includeHazards')} 
            />
            <OptionRow 
                icon={<PieChart className="w-4 h-4"/>} 
                label="Statistik & Auswertung" 
                checked={options.includeStatistics} 
                onChange={() => toggle('includeStatistics')} 
            />
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t flex justify-end">
          <Button data-tutorial="pdf.optionen-fertig" variant="primary" onClick={onClose}>Fertig</Button>
        </div>
      </div>
    </div>
  );
};

const OptionRow = ({ icon, label, checked, onChange }: { icon: React.ReactNode, label: string, checked: boolean, onChange: () => void }) => (
    <div 
        onClick={onChange}
        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${checked ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-300'}`}
    >
        <div className="flex items-center gap-3 text-sm font-bold text-slate-700">
            <div className={`p-1.5 rounded-lg ${checked ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                {icon}
            </div>
            {label}
        </div>
        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${checked ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
            {checked && <div className="w-2 h-2 bg-white rounded-full" />}
        </div>
    </div>
);
